import React, { useState, useEffect, useMemo } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
import {
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Card,
  MenuItem,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const TIME_FILTER_OPTIONS = [
  { label: "1 Year", value: "1_YEAR" },
  { label: "All Time", value: "ALL_TIME" },
];

const MarketsDataByMonth = ({
  title = "Market Data Stats",
  subtitle = "",
  monthlyData = [],
  loading = false,
  initialItemType = null,
  initialTimeFilter = null,
  onFilterChange = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true }); // < 600px
  const [itemTypes, setItemTypes] = useState([]);
  const [selectedItemType, setSelectedItemType] = useState("");
  const [isHydrated, setIsHydrated] = useState(false);

  // Always start with default time filter to match SSR
  const [timeFilter, setTimeFilter] = useState(TIME_FILTER_OPTIONS[0]);

  // Mark component as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Initialize time filter from URL after hydration completes
  useEffect(() => {
    if (!isHydrated) return;

    if (initialTimeFilter) {
      const found = TIME_FILTER_OPTIONS.find(
        (opt) => opt.value.toLowerCase() === initialTimeFilter.toLowerCase()
      );
      if (found) {
        setTimeFilter(found);
      }
    }
  }, [isHydrated, initialTimeFilter]);

  // Extract item types when monthlyData changes
  useEffect(() => {
    if (monthlyData.length > 0) {
      const uniqueTypes = [
        ...new Set(monthlyData.map((item) => item.item_type)),
      ].sort();
      const formattedTypes = uniqueTypes.map((type) => ({ label: type }));
      setItemTypes(formattedTypes);

      // Set initial selected item type from URL or default to DIAMOND
      if (formattedTypes.length > 0 && !selectedItemType) {
        let initialItem = null;

        // Try to find item from URL parameter
        if (initialItemType) {
          initialItem = formattedTypes.find((t) => t.label === initialItemType);
        }

        // Fallback to DIAMOND or first available
        if (!initialItem) {
          const diamondOption = formattedTypes.find(
            (t) => t.label === "DIAMOND"
          );
          initialItem = diamondOption || formattedTypes[0];
        }

        setSelectedItemType(initialItem);
      }
    }
  }, [monthlyData, selectedItemType]);

  // Memoize filtered data generation - expensive operation with date parsing and placeholder creation
  const filteredData = useMemo(() => {
    if (!selectedItemType || !monthlyData || monthlyData.length === 0) {
      return [];
    }

    const selectedLabel = selectedItemType.label || "";

    // Filter out any invalid month values first
    const validMonthlyData = monthlyData.filter(
      (item) => item.month && item.month !== "month"
    );

    if (validMonthlyData.length === 0) {
      return [];
    }

    // Parse dates explicitly to avoid timezone issues with YYYY-MM format
    const allDates = validMonthlyData.map((item) => {
      const [year, month] = item.month.split('-').map(Number);
      return new Date(year, month - 1, 1); // month is 0-indexed in Date constructor
    });
    const maxDateInDataset = new Date(Math.max(...allDates));

    // Filter data by selected item type
    const itemFilteredData = validMonthlyData.filter(
      (item) => item.item_type === selectedLabel
    );

    // Create a map of existing data points by month for this item
    const dataByMonth = {};
    itemFilteredData.forEach((item) => {
      dataByMonth[item.month] = item;
    });

    // Determine date range based on time filter
    let startDate;
    if (timeFilter.value === "1_YEAR") {
      // For 1 Year: show exactly 12 months ending at the latest month in dataset
      startDate = new Date(maxDateInDataset);
      startDate.setMonth(startDate.getMonth() - 11); // Go back 11 months (12 total including current)
    } else {
      // For ALL_TIME: use the earliest month for this specific item
      if (itemFilteredData.length > 0) {
        const itemDates = itemFilteredData.map((item) => {
          const [year, month] = item.month.split('-').map(Number);
          return new Date(year, month - 1, 1);
        });
        const minDate = new Date(Math.min(...itemDates));
        startDate = minDate;
      } else {
        // No data for this item, use dataset range
        const minDateInDataset = new Date(Math.min(...allDates));
        startDate = minDateInDataset;
      }
    }

    // Generate a complete month sequence from startDate to maxDateInDataset
    const completeData = [];
    let iterationDate = new Date(startDate);

    while (iterationDate <= maxDateInDataset) {
      const monthStr = iterationDate.toISOString().substring(0, 7); // Format: YYYY-MM

      if (dataByMonth[monthStr]) {
        // Use existing data if available
        completeData.push(dataByMonth[monthStr]);
      } else {
        // Create a placeholder for missing months
        // total_transaction_value: 0 (shows as 0 bar)
        // avg_price_per_unit: null (line will skip/connect to next valid point)
        completeData.push({
          month: monthStr,
          item_type: selectedLabel,
          total_transaction_value: 0,
          avg_price_per_unit: null,
          total_sales: 0,
          total_units: 0,
          transaction_count: 0,
        });
      }

      // Move to next month
      iterationDate.setMonth(iterationDate.getMonth() + 1);
    }

    return completeData;
  }, [selectedItemType, monthlyData, timeFilter]);

  const handleItemTypeChange = (event, newValue) => {
    if (newValue === null && itemTypes.length > 0) {
      setSelectedItemType(itemTypes[0]);
      onFilterChange({
        item: itemTypes[0].label,
        time: timeFilter.value.toLowerCase(),
      });
    } else {
      setSelectedItemType(newValue);
      onFilterChange({
        item: newValue?.label,
        time: timeFilter.value.toLowerCase(),
      });
    }
  };

  const handleTimeFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setTimeFilter(newValue);
      onFilterChange({
        item: selectedItemType?.label,
        time: newValue.value.toLowerCase(),
      });
    }
  };

  // Format large numbers compactly (e.g., 260k, 1.2M)
  // Decimals only shown if value < 10
  const formatCompactNumber = (value) => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return millions < 10
        ? `${millions.toFixed(1)}M`
        : `${millions.toFixed(0)}M`;
    }
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands < 10
        ? `${thousands.toFixed(1)}k`
        : `${thousands.toFixed(0)}k`;
    }
    return value.toFixed(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Customize the tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get the data point for additional info (Qty Sold)
      const dataPoint = payload[0]?.payload;

      return (
        <Card sx={{ p: 1.5, boxShadow: 2, bgcolor: "background.paper" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: entry.color,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: entry.color,
                    mr: 1,
                    borderRadius: 0.5,
                  }}
                />
                {entry.name}:{" "}
                {typeof entry.value === "number"
                  ? entry.value.toLocaleString()
                  : entry.value}
              </Typography>
            </Box>
          ))}
          {/* Add Qty Sold - not part of chart, just tooltip */}
          {dataPoint && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Qty Sold: {dataPoint.total_sales?.toLocaleString() || 0}
              </Typography>
            </Box>
          )}
        </Card>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <StorefrontIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h5">{title}</Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              id="item-type-filter-chart"
              options={itemTypes}
              value={selectedItemType}
              onChange={handleItemTypeChange}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Item Type"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <FilterListIcon
                          sx={{ color: "action.active", mr: 1 }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Time Period"
              value={timeFilter.value}
              onChange={(e) => {
                const selected = TIME_FILTER_OPTIONS.find(opt => opt.value === e.target.value);
                handleTimeFilterChange(null, selected);
              }}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <CalendarMonthIcon
                    sx={{ color: "action.active", mr: 1, ml: 1 }}
                  />
                ),
              }}
            >
              {TIME_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {selectedItemType && (
              <Chip
                label={selectedItemType.label}
                color="primary"
                variant="outlined"
                size="medium"
              />
            )}
            <Chip
              label={timeFilter.label}
              color="secondary"
              variant="outlined"
              size="medium"
            />
          </Grid>
        </Grid>
      </Box>

      {filteredData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
          <ComposedChart
            data={filteredData}
            margin={{
              top: 20,
              right: isMobile ? 5 : 30,
              left: isMobile ? 5 : 20,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.6} />
            <XAxis
              dataKey="month"
              tick={{
                fill: theme.palette.text.primary,
                fontSize: isMobile ? 10 : 12,
              }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{
                fill: theme.palette.text.primary,
                fontSize: isMobile ? 10 : 12,
              }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              tickFormatter={formatCompactNumber}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{
                fill: theme.palette.text.primary,
                fontSize: isMobile ? 10 : 12,
              }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              tickFormatter={formatCompactNumber}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: isMobile ? "10px" : "10px",
                fontSize: isMobile ? "12px" : "14px",
              }}
              iconSize={isMobile ? 12 : 14}
              height={isMobile ? 40 : 36}
            />
            <Bar
              dataKey="total_transaction_value"
              name="Total Sales $"
              yAxisId="left"
              fill={alpha(theme.palette.primary.main, 0.7)}
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="avg_price_per_unit"
              name="Avg Price"
              yAxisId="right"
              stroke={theme.palette.secondary.main}
              strokeWidth={2}
              dot={{ r: 4, fill: theme.palette.secondary.main }}
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Volume Mini-Chart */}
        <Box sx={{ mt: 3 }}>
          <ResponsiveContainer width="100%" height={isMobile ? 80 : 120}>
            <ComposedChart
              data={filteredData}
              margin={{
                top: 5,
                right: isMobile ? 5 : 30,
                left: isMobile ? 5 : 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis
                dataKey="month"
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: 10,
                }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                height={30}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={formatCompactNumber}
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: 10,
                }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={false}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(0,0,0,0.1)" }}
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const dataPoint = payload[0]?.payload;
                    return (
                      <Card
                        sx={{ p: 1, boxShadow: 2, bgcolor: "background.paper" }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.success.main }}
                        >
                          <strong>Volume:</strong>{" "}
                          {dataPoint?.total_sales?.toLocaleString() || 0} units
                        </Typography>
                      </Card>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="total_sales"
                name="Volume (Qty Sold)"
                yAxisId="left"
                fill={alpha(theme.palette.success.main, 0.7)}
                radius={[2, 2, 0, 0]}
              />
              <Legend
                wrapperStyle={{
                  paddingTop: "10px",
                }}
                iconSize={isMobile ? 10 : 14}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        </>
      ) : (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No data available for the selected item type
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MarketsDataByMonth;
